/** App Store 公開URL（記事・フロート等のUTM付与の基準） */
export const APP_STORE_URL = 'https://apps.apple.com/jp/app/social-trip/id6753195409'

/** Google Play 公開URL（パッケージ ID: com.mycompany.triplink） */
export const GOOGLE_PLAY_PACKAGE_ID = 'com.mycompany.triplink'
export const GOOGLE_PLAY_URL = `https://play.google.com/store/apps/details?id=${GOOGLE_PLAY_PACKAGE_ID}`

function buildGooglePlayUrl(utm: Record<string, string>): string {
  const referrer = new URLSearchParams(utm).toString()
  const params = new URLSearchParams({ id: GOOGLE_PLAY_PACKAGE_ID })
  if (referrer) {
    params.set('referrer', referrer)
  }
  return `https://play.google.com/store/apps/details?${params.toString()}`
}

/**
 * トップページ・ヒーロー内の App Store メインCTA用。
 * フローティング（floating_cta）・記事本文（article）と区別する。
 */
export function appStoreUrlHomeHeroCta(): string {
  const params = new URLSearchParams({
    utm_source: 'socialtrip_web',
    utm_medium: 'home_hero',
    utm_campaign: 'top_landing',
    utm_content: 'app_store_download',
  })
  return `${APP_STORE_URL}?${params.toString()}`
}

export function googlePlayUrlHomeHeroCta(): string {
  return buildGooglePlayUrl({
    utm_source: 'socialtrip_web',
    utm_medium: 'home_hero',
    utm_campaign: 'top_landing',
    utm_content: 'google_play_download',
  })
}

/**
 * ヘッダー「アプリをダウンロード」用
 */
export function appStoreUrlHeaderCta(): string {
  const params = new URLSearchParams({
    utm_source: 'socialtrip_web',
    utm_medium: 'header',
    utm_campaign: 'site_nav',
    utm_content: 'app_store_download',
  })
  return `${APP_STORE_URL}?${params.toString()}`
}

export function googlePlayUrlHeaderCta(): string {
  return buildGooglePlayUrl({
    utm_source: 'socialtrip_web',
    utm_medium: 'header',
    utm_campaign: 'site_nav',
    utm_content: 'google_play_download',
  })
}

/**
 * 記事本文のCTA用（従来どおり）
 * utm_medium=article でイン記事ブロックと一致
 */
export function appStoreUrlArticleBodyCta(articleId: string | number): string {
  const params = new URLSearchParams({
    utm_source: 'socialtrip_web',
    utm_medium: 'article',
    utm_campaign: 'article_cta',
    utm_id: String(articleId),
  })
  return `${APP_STORE_URL}?${params.toString()}`
}

export function googlePlayUrlArticleBodyCta(articleId: string | number): string {
  return buildGooglePlayUrl({
    utm_source: 'socialtrip_web',
    utm_medium: 'article',
    utm_campaign: 'article_cta',
    utm_content: 'google_play_download',
    utm_id: String(articleId),
  })
}

/**
 * 記事本文「中盤」差し込み CTA 用（マーカー <!-- SOCIAL_TRIP_MID_CTA --> 位置）。
 * 本文末尾のブロック（utm_medium=article）と計測を分離する。
 */
export function appStoreUrlArticleMidCta(articleId: string | number): string {
  const params = new URLSearchParams({
    utm_source: 'socialtrip_web',
    utm_medium: 'article_mid',
    utm_campaign: 'article_cta',
    utm_content: 'body_mid_slot',
    utm_id: String(articleId),
  })
  return `${APP_STORE_URL}?${params.toString()}`
}

export function googlePlayUrlArticleMidCta(articleId: string | number): string {
  return buildGooglePlayUrl({
    utm_source: 'socialtrip_web',
    utm_medium: 'article_mid',
    utm_campaign: 'article_cta',
    utm_content: 'body_mid_slot',
    utm_id: String(articleId),
  })
}

/**
 * 画面下部フローティングCTA用。
 * 記事ページでは utm_id に記事IDを載せ、utm_medium=floating_cta で本文CTAと区別する。
 */
export function appStoreUrlFloatingCta(pathname: string, articleId?: string): string {
  if (articleId) {
    const params = new URLSearchParams({
      utm_source: 'socialtrip_web',
      utm_medium: 'floating_cta',
      utm_campaign: 'article_cta',
      utm_content: 'sticky',
      utm_id: articleId,
    })
    return `${APP_STORE_URL}?${params.toString()}`
  }
  const segment = pathnameToFloatingUtmCampaign(pathname);
  const params = new URLSearchParams({
    utm_source: 'socialtrip_web',
    utm_medium: 'floating_cta',
    utm_campaign: segment,
    utm_content: 'sticky',
  });
  return `${APP_STORE_URL}?${params.toString()}`
}

export function googlePlayUrlFloatingCta(pathname: string, articleId?: string): string {
  if (articleId) {
    return buildGooglePlayUrl({
      utm_source: 'socialtrip_web',
      utm_medium: 'floating_cta',
      utm_campaign: 'article_cta',
      utm_content: 'sticky',
      utm_id: articleId,
    })
  }
  const segment = pathnameToFloatingUtmCampaign(pathname);
  return buildGooglePlayUrl({
    utm_source: 'socialtrip_web',
    utm_medium: 'floating_cta',
    utm_campaign: segment,
    utm_content: 'sticky',
  })
}

/**
 * 診断結果ページのマーケ計測用 utm_campaign。
 * ページ番号指定時は diagnosis/result2 形式（マーケ指定）。非数値キーは従来の result_* を維持。
 */
export function diagnosisResultUtmCampaign(resultKey: string | number): string {
  const key = String(resultKey);
  if (/^\d+$/.test(key)) {
    return `diagnosis/result${key}`;
  }
  return `result_${key}`;
}

function pathnameToFloatingUtmCampaign(pathname: string): string {
  const diagnosisMatch = pathname.match(/^\/diagnosis-result(\d+)\/?$/);
  if (diagnosisMatch) {
    return `diagnosis/result${diagnosisMatch[1]}`;
  }
  if (!pathname || pathname === '/') {
    return 'home';
  }
  return pathname.replace(/^\//, '').replace(/\//g, '_').slice(0, 120) || 'other';
}

/**
 * 旅行タイプ診断結果ページ用（App Store / Google Play CTA）
 * utm_campaign=diagnosis/result{N}、utm_id=N（ページ番号）
 */
export function appStoreUrlDiagnosisResultCta(resultKey: string | number): string {
  const key = String(resultKey);
  const params = new URLSearchParams({
    utm_source: 'socialtrip_web',
    utm_medium: 'diagnosis_result',
    utm_campaign: diagnosisResultUtmCampaign(resultKey),
    utm_content: 'app_store_download',
  });
  if (/^\d+$/.test(key)) {
    params.set('utm_id', key);
  }
  return `${APP_STORE_URL}?${params.toString()}`;
}

export function googlePlayUrlDiagnosisResultCta(resultKey: string | number): string {
  const key = String(resultKey);
  const utm: Record<string, string> = {
    utm_source: 'socialtrip_web',
    utm_medium: 'diagnosis_result',
    utm_campaign: diagnosisResultUtmCampaign(resultKey),
    utm_content: 'google_play_download',
  };
  if (/^\d+$/.test(key)) {
    utm.utm_id = key;
  }
  return buildGooglePlayUrl(utm);
}
